import { APP_INITIALIZER, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { AuthConfig, AuthHttpInterceptor, AuthModule, AuthService } from '@auth0/auth0-angular';
import { environment } from '../environments/environment';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from './navigation/header/header.component';

import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DataViewModule } from 'primeng/dataview';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { GalleriaModule } from 'primeng/galleria';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { SliderModule } from 'primeng/slider';
import { PanelModule } from 'primeng/panel';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { TabMenuModule } from 'primeng/tabmenu';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { RippleModule } from 'primeng/ripple';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ColorPickerModule } from 'primeng/colorpicker';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TreeSelectModule } from 'primeng/treeselect';
import { AccordionModule } from 'primeng/accordion';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { CalendarModule } from 'primeng/calendar';

import { ConfirmationService, MessageService } from 'primeng/api';

import { HighlightModule, HIGHLIGHT_OPTIONS, HighlightOptions } from 'ngx-highlightjs';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AngularSplitModule } from 'angular-split';

import { MainComponent } from './main/components/main/main.component';
import { HomeComponent } from './main/components/home/home.component';
import { FileTreeComponent } from './main/components/file-tree/file-tree.component';
import { CodeComponent } from './main/components/code/code.component';
import { DiagramComponent } from './main/components/diagram/diagram.component';
import { VisualizationModule } from '@jax-data-science/sci-image-visualizer';
import { DynamicTableComponent } from './main/components/dynamic-table/dynamic-table.component';
import { FileViewerComponent } from './main/components/file-viewer/file-viewer.component';
import { ResultsSummaryComponent } from './main/components/results-summary/results-summary.component';
import { ProcessComponent } from './main/components/process/process.component';
import { DirectorySubmitDialogComponent } from './main/components/process/directory-submit-dialog/directory-submit-dialog.component';
import { CreateRuleDialogComponent } from './main/components/process/create-rule-dialog/create-rule-dialog.component';
import { RulesComponent } from './main/components/rules/rules.component';
import { ResultSearchComponent } from './main/components/results/result-search.component';
import { SegmentationComponent } from './main/components/process/segmentation/segmentation.component';
import { ConvertComponent } from './main/components/process/convert/convert.component';
import { HtquantComponent } from './main/components/process/htquant/htquant.component';
import { SiftComponent } from './main/components/process/sift/sift.component';
import { KidneyClassifierComponent } from './main/components/process/kidney-classifier/kidney-classifier.component';
import { ProcessControlComponent } from './main/components/process/process-control/process-control.component';
import { EDOFComponent } from './main/components/process/edof/edof.component';
import { FooterComponent } from './navigation/footer/footer.component';
import { DetailsComponent } from './main/components/details/details.component';
import { SearchComponent } from './main/components/search/search.component';
import { MetadataFilterRowComponent } from './main/components/metadata-filter-row/metadata-filter-row.component';
import { SubmitButtonComponent } from './shared/submit-button/submit-button.component';

import { InstanceofPipe } from './main/pipes/instanceof.pipe';
import { FormatFileSizePipe } from './main/pipes/format-file-size.pipe';
import { ProcessStatusComponent } from './main/components/process/process-status/process-status/process-status.component';
import { ProcessStatusItemComponent } from './main/components/process/process-status/process-status-item/process-status-item.component';
import { getSaver, SAVER } from './download/saver.provider';
import { DeconvolutionComponent } from './main/components/process/deconvolution/deconvolution.component';
import { YolosegdetectComponent } from './main/components/process/yolosegdetect/yolosegdetect.component';
import { ImageProcessingComponent } from './main/components/process/image-processing/image-processing.component';
import { CellposeComponent } from './main/components/process/cellpose/cellpose.component';
import { QupathScriptComponent } from './main/components/process/qupath-script/qupath-script.component';
import { ImageJMacroComponent } from './main/components/process/imagej-macro/imagej-macro.component';
import { StorageScriptPickerComponent } from './main/components/process/qupath-script/storage-script-picker/storage-script-picker.component';
import { RegionPickerComponent } from './main/components/region-picker/region-picker.component';
import { ParameterRowComponent } from './main/components/parameter-row/parameter-row.component';
import { RetinalLayerComponent } from './main/components/process/retinal-layer/retinal-layer.component';
import { StainAdapterComponent } from './main/components/process/stain-adapter/stain-adapter.component';
import { NgcCookieConsentConfig, provideNgcCookieConsent } from 'ngx-cookieconsent';
import { AuthInterceptor } from './main/auth.interceptor';
import { ProcessingPipelineModule } from './main/components/processing-pipeline/processing-pipeline.module';
import { IMAGE_STATE_PORT } from '@jax-data-science/sci-image-visualizer';
import { TILE_ACCESS_PORT } from '@jax-data-science/sci-image-visualizer';
import { REGION_IO_PORT } from '@jax-data-science/sci-image-visualizer';
import { VIZ_CONFIG } from '@jax-data-science/sci-image-visualizer';
import { PREFERENCES_PORT } from '@jax-data-science/sci-image-visualizer';
import { SPATIAL_DATA_PORT, SpatialDataHttpService } from '@jax-data-science/sci-image-visualizer';
import { ImageStateAdapter } from './visualization-adapters/image-state.adapter';
import { TileAccessAdapter } from './visualization-adapters/tile-access.adapter';
import { RegionIoAdapter } from './visualization-adapters/region-io.adapter';
import { PreferencesAdapter } from './visualization-adapters/preferences.adapter';
import { provideJaxToolbarTools, setJaxToolbarModelFetcher } from './jax-tools.provider';
import { firstValueFrom } from 'rxjs';


export function selectAuthInterceptor() {
  return environment.cloud ? AuthHttpInterceptor : AuthInterceptor;
}

export const AuthInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: selectAuthInterceptor(),
  multi: true,
};

const config: AuthConfig = {
  ...environment.auth,
  httpInterceptor: {
    allowedList: [
      // any/all calls to endpoints in CIMG API
      ...Object.values(environment.securedURLs).map((url) => {
        return {
          uri: `${url}/*`,
          allowAnonymous: true, // we still want anonymous/non-authenticated users to search
        };
      }),
    ],
  },
};

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: environment.cookieDomain // it is mandatory to set a domain, for cookies to work properly (see https://goo.gl/S2Hy2A)
  },
  position: 'bottom',
  palette: {
    popup: {
      background: '#E5E7EB'
    },
    button: {
      background: '#f1d600'
    }
  },
  theme: 'classic',
  type: 'opt-out',
  content: {
    message: `Welcome! Our website uses cookies exclusively for usage analytics and reporting metrics to our
    stakeholders and funding agencies about site usage. These cookies also help us understand how visitors
    interact with our site, enabling us to improve your browsing experience and the content we offer. We
    respect your privacy and ensure that all data collected is anonymized and used solely to guide our
    enhancements. Additionally, we do not use this data for marketing purposes nor do we share any user
    information with third parties.
    By clicking "Accept," you consent to our use of cookies for these purposes. For more detailed information
    on the cookies we use and how we handle your data, please review our`,
    dismiss: 'Allow cookies',
    deny: 'Decline',
    allow: 'Accept',
    link: 'Privacy Policy.',
    href: 'https://www.jax.org/privacy-policy',
    policy: 'Cookie Policy'
  }
}

@NgModule({
  declarations: [
    AppComponent,
    CodeComponent,
    HeaderComponent,
    MainComponent,
    HomeComponent,
    FileTreeComponent,
    DiagramComponent,
    DynamicTableComponent,
    ResultsSummaryComponent,
    FileViewerComponent,
    ProcessComponent,
    DirectorySubmitDialogComponent,
    CreateRuleDialogComponent,
    RulesComponent,
    ResultSearchComponent,
    SegmentationComponent,
    ConvertComponent,
    HtquantComponent,
    SiftComponent,
    ProcessControlComponent,
    EDOFComponent,
    KidneyClassifierComponent,
    DeconvolutionComponent,
    YolosegdetectComponent,
    CellposeComponent,
    QupathScriptComponent,
    ImageJMacroComponent,
    StorageScriptPickerComponent,
    RegionPickerComponent,
    ParameterRowComponent,
    ImageProcessingComponent,
    RetinalLayerComponent,
    StainAdapterComponent,
    SubmitButtonComponent,
    FooterComponent,
    DetailsComponent,
    InstanceofPipe,
    FormatFileSizePipe,
    ProcessStatusComponent,
    ProcessStatusItemComponent,
    SearchComponent,
    MetadataFilterRowComponent
  ],
  imports: [
    AuthModule.forRoot(config),
    RouterModule.forRoot([]),
    BrowserModule,
    BrowserAnimationsModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    ContextMenuModule,
    DataViewModule,
    DividerModule,
    DropdownModule,
    DynamicDialogModule,
    FormsModule,
    GalleriaModule,
    HighlightModule,
    HttpClientModule,
    InputTextModule,
    InputTextareaModule,
    InputNumberModule,
    PanelModule,
    PdfViewerModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    ReactiveFormsModule,
    RippleModule,
    RouterModule,
    SelectButtonModule,
    SliderModule,
    TabMenuModule,
    TableModule,
    PaginatorModule,
    ToastModule,
    ToggleButtonModule,
    ToolbarModule,
    TooltipModule,
    TreeModule,
    AngularSplitModule,
    ColorPickerModule,
    InputSwitchModule,
    DialogModule,
    FileUploadModule,
    OverlayPanelModule,
    TreeSelectModule,
    AccordionModule,
    BreadcrumbModule,
    CalendarModule,
    ProcessingPipelineModule,
    VisualizationModule,
  ],
  providers: [
    AuthInterceptorProvider,
    // The JAX toolbar tools (YOLO detection, retinal-layer segmentation). They
    // are not in sci-image-visualizer any more -- its checkpoints are closed, so
    // it ships the contribution point and this registers the tools into it.
    //
    // `browserModelServer` is undefined in environments that should not offer
    // them; the plugin then resolves no weights and the toolbar renders no YOLO or
    // retinal buttons, so this stays a single unconditional call.
    //
    // The same call also contributes the "Digital Pathology - DIANNE" toolbar tool
    // (jit-extensions 0.5.0+, a dialog tool on the visualizer's TOOLBAR_TOOLS): a button
    // next to the image processing pipeline, in the Image view, that opens the DIANNE
    // dialog. It needs no model server, so it stays even without `browserModelServer`,
    // and no options: its base URL and auth come from VIZ_CONFIG and TILE_ACCESS_PORT,
    // bound below, and it reads STQ `.h5ad` features through the existing tile endpoints.
    ...provideJaxToolbarTools(),
    // The model server requires a JAX Auth0 bearer token, and the checkpoints
    // are fetched by the model libraries with `fetch` rather than HttpClient --
    // so the auth interceptor never sees them. This attaches the token to those
    // downloads instead.
    //
    // A bearer, not a cookie: three of the four instances reach the model server
    // cross-origin, where a host-scoped oauth2-proxy session cookie is not sent.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (auth: AuthService) => () => {
        const authedFetch = async (url: string, init?: RequestInit) => {
          const token = await firstValueFrom(auth.getAccessTokenSilently());
          // Normalize through `Headers` rather than spreading. `RequestInit.headers`
          // is a `HeadersInit` -- a record, a `Headers` instance, or an array of
          // pairs -- and only the record form survives a spread: `Headers` has no
          // own enumerable properties, so it spreads to `{}`, and an array spreads
          // to `{0: [...]}`. Either way the caller's headers are silently dropped,
          // which would quietly break a library that sends its own Range or
          // cache-control header on a checkpoint download.
          const headers = new Headers(init?.headers);
          headers.set('Authorization', `Bearer ${token}`);
          return fetch(url, { ...init, headers });
        };
        // Two seams, deliberately: the toolbar plugin's, and the pipeline's own.
        // The pipeline does not depend on the plugin, so it cannot share its
        // module state — but both need the same token.
        // One install, not two: the pipeline engines now live in the same package
        // as the toolbar tools and share its fetcher.
        setJaxToolbarModelFetcher(authedFetch);
      },
      deps: [AuthService],
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: <HighlightOptions>{
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          xml: () => import('highlight.js/lib/languages/xml'),
          json: () => import('highlight.js/lib/languages/json'),
          // QuPath scripts (.groovy) - code-panel passes [languages]="['groovy']" explicitly
          // rather than relying on auto-detect among all three registered languages, since a
          // short/ambiguous script could otherwise get misclassified as something else.
          groovy: () => import('highlight.js/lib/languages/groovy'),
          // ImageJ macros (.ijm) - C-like, so highlight.js's JavaScript grammar fits.
          javascript: () => import('highlight.js/lib/languages/javascript'),
        },
      },
    },
    ConfirmationService,
    MessageService,
    DialogService,
    { provide: SAVER, useFactory: getSaver },
    // Host adapters for the visualization library's ports, so the library never
    // imports MainState/MainFacade/FilesService/environment directly. The internal
    // backend wiring (VISUALIZER / REGION_EDITOR_API / CHANNEL_HISTOGRAM_API ->
    // RoutingVisualizerService) now lives in VisualizationModule, so it isn't
    // repeated here — importing the module is enough.
    { provide: IMAGE_STATE_PORT, useExisting: ImageStateAdapter },
    { provide: TILE_ACCESS_PORT, useExisting: TileAccessAdapter },
    { provide: REGION_IO_PORT, useExisting: RegionIoAdapter },
    { provide: PREFERENCES_PORT, useExisting: PreferencesAdapter },
    // Spatial omics: jit-service's /spatial routes speak the library's wire format, so
    // its reference adapter is the port. SpatialOmicsService points it at the server.
    SpatialDataHttpService,
    { provide: SPATIAL_DATA_PORT, useExisting: SpatialDataHttpService },
    // The cellpose-SAM segmenter for the box→crop→cellpose tool is provided by
    // the library itself (CellposeSegmenterService, cellpose-js) — no host
    // wiring needed. The pipeline's CellposeEngine reuses that same instance.
    {
      provide: VIZ_CONFIG,
      useValue: {
        slideCropServer: environment.slideCropServer,
        // Open the Region Editor dialog at the current width of the right panel.
        regionEditorWidthSelector: '.right-panel-area',
      },
    },
    // The library's SAM model registry now ships the hosted ONNX URLs as
    // defaults (jit-ui#90), so no host wiring is needed. To point at different
    // hosting, call setSamModelUrls(...) here.
    provideNgcCookieConsent(cookieConfig),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
